import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, CheckCircle, Zap, Shield, Cpu, TrendingUp, ArrowRight, Phone, Sparkles } from 'lucide-react';

export default function InteligenciaArtificial() {
  const caracteristicas = [
    {
      icon: Brain,
      titulo: 'IA Empresarial',
      descripcion: 'Modelos de lenguaje grandes (LLMs) y IA generativa con seguridad y gobernanza empresarial.'
    },
    {
      icon: Shield,
      titulo: 'Private Cloud AI',
      descripcion: 'Infraestructura dedicada para entrenar y ejecutar modelos de IA sin exponer datos sensibles.'
    },
    {
      icon: Cpu,
      titulo: 'Aceleración GPU',
      descripcion: 'Servidores optimizados con NVIDIA GPUs para cargas de trabajo de IA y machine learning.'
    },
    {
      icon: Zap,
      titulo: 'Despliegue Rápido',
      descripcion: 'Implementación en semanas, no meses, con configuraciones prevalidadas y soporte especializado.'
    }
  ];

  const servicios = [
    {
      categoria: 'HPE AI Services',
      descripcion: 'Suite completa de servicios de inteligencia artificial gestionados en la nube híbrida',
      capacidades: [
        'Large Language Models (LLMs) preentrenados',
        'Fine-tuning con datos corporativos',
        'Inferencia escalable y optimizada',
        'Integración con aplicaciones empresariales'
      ],
      casos: 'Chatbots inteligentes, análisis de documentos, asistentes virtuales, automatización de procesos'
    },
    {
      categoria: 'HPE Private Cloud AI',
      descripcion: 'Infraestructura dedicada para IA con control total sobre datos y modelos',
      capacidades: [
        'Clusters de GPUs NVIDIA (A100, H100)',
        'Almacenamiento de alto rendimiento para datasets',
        'Networking optimizado para entrenamiento distribuido',
        'Orquestación con Kubernetes y MLOps'
      ],
      casos: 'Entrenamiento de modelos propietarios, investigación de IA, visión por computadora, procesamiento de lenguaje natural'
    },
    {
      categoria: 'HPE GreenLake for AI',
      descripcion: 'Consumo as-a-service de infraestructura de IA sin inversión inicial',
      capacidades: [
        'Pago por uso (pay-per-use)',
        'Escalamiento automático de recursos',
        'Gestión centralizada desde GreenLake Console',
        'Monitoreo y optimización con InfoSight AI'
      ],
      casos: 'Proyectos piloto de IA, cargas de trabajo variables, expansión rápida de capacidad'
    }
  ];

  const beneficios = [
    'Hasta 10x más rápido en entrenamiento de modelos vs infraestructura tradicional',
    'Reducción de 60% en costos operativos con consumo as-a-service',
    'Seguridad y cumplimiento normativo garantizados',
    'Time-to-market reducido de meses a semanas',
    'Soporte especializado de expertos en IA y HPC',
    'Integración nativa con plataformas MLOps (Kubeflow, MLflow)',
    'Escalamiento desde POC hasta producción sin cambios de arquitectura'
  ];

  const useCases = [
    {
      titulo: 'Asistentes Virtuales Inteligentes',
      descripcion: 'Chatbots con LLMs que entienden contexto corporativo y responden con información específica de tu negocio.',
      icon: Sparkles
    },
    {
      titulo: 'Análisis Predictivo',
      descripcion: 'Modelos de ML para predecir demanda, detectar anomalías y optimizar operaciones en tiempo real.',
      icon: TrendingUp
    },
    {
      titulo: 'Visión por Computadora',
      descripcion: 'Reconocimiento de imágenes, inspección de calidad automatizada y análisis de video con deep learning.',
      icon: Brain
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1635070041078-e363dbe005cb?q=80&w=2370)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/90 via-indigo-900/85 to-purple-900/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-purple-600/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-purple-300 font-semibold">HPE AI Services + Private Cloud AI</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Inteligencia Artificial Empresarial
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              Implementa IA generativa, machine learning y modelos de lenguaje con infraestructura segura y gobernanza empresarial
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/contacto" 
                className="bg-purple-600 text-white px-8 py-4 rounded-lg hover:bg-purple-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
              >
                Consulta con Expertos en IA <ArrowRight className="ml-2" />
              </Link>
              <a 
                href="#servicios" 
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-colors text-lg font-semibold inline-flex items-center justify-center border border-white/30"
              >
                Ver Servicios de IA
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                IA con seguridad y control corporativo
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                La <strong>inteligencia artificial</strong> está transformando industrias, pero implementarla 
                de forma segura requiere infraestructura especializada. Con <strong>HPE AI Services</strong> y 
                <strong> HPE Private Cloud AI</strong>, obtienes todo lo necesario: desde modelos preentrenados 
                hasta infraestructura dedicada con GPUs NVIDIA de última generación.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                A diferencia de clouds públicos donde compartes recursos y expones datos, nuestra solución 
                te da control total: tus modelos, tus datos, tu infraestructura. Ideal para casos de uso 
                que requieren confidencialidad, cumplimiento normativo o personalización profunda.
              </p>
              <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded-lg">
                <p className="text-gray-800 font-semibold mb-2">
                  <Brain className="inline mr-2 text-purple-600" size={24} />
                  ¿Por qué Private Cloud AI?
                </p>
                <p className="text-gray-600">
                  Los modelos de IA aprenden de tus datos. En clouds públicos, existe riesgo de fuga de 
                  información. Private Cloud AI garantiza que tus datos sensibles nunca salgan de tu 
                  infraestructura controlada, cumpliendo con regulaciones estrictas (GDPR, HIPAA, PCI-DSS).
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1655720828018-edd2daec9349?q=80&w=2080" 
                alt="AI Infrastructure"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Características */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Capacidades Clave
            </h2>
            <p className="text-xl text-gray-600">
              Todo lo que necesitas para implementar IA a escala empresarial
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caracteristicas.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow">
                  <div className="bg-purple-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-purple-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.titulo}</h3>
                  <p className="text-gray-600">{item.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Portafolio de Servicios de IA
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Desde servicios gestionados hasta infraestructura dedicada
            </p>
          </div>
          <div className="space-y-8">
            {servicios.map((servicio, index) => (
              <div 
                key={index}
                className="bg-gradient-to-r from-purple-50 to-white border border-purple-200 rounded-xl p-8 hover:shadow-xl transition-all"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="bg-purple-100 p-3 rounded-lg">
                        <Brain className="text-purple-600" size={28} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{servicio.categoria}</h3>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 text-lg">{servicio.descripcion}</p>
                    <div className="mb-4">
                      <span className="text-sm font-semibold text-gray-700">Capacidades incluidas:</span>
                      <ul className="mt-2 space-y-2">
                        {servicio.capacidades.map((capacidad, idx) => (
                          <li key={idx} className="flex items-center space-x-2 text-gray-600">
                            <CheckCircle className="text-hpe-green flex-shrink-0" size={16} />
                            <span>{capacidad}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-6 border border-purple-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Casos de uso ideales:</h4>
                    <p className="text-gray-600 text-sm">{servicio.casos}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Casos de Uso */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Aplicaciones Prácticas
            </h2>
            <p className="text-xl text-gray-600">
              Implementaciones reales de IA en empresas
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg">
                  <div className="bg-purple-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-purple-600" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{useCase.titulo}</h3>
                  <p className="text-gray-600">{useCase.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-br from-purple-600 to-indigo-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Ventajas Competitivas
              </h2>
              <p className="text-xl text-purple-100 mb-8">
                Organizaciones que implementan IA con HPE experimentan:
              </p>
              <ul className="space-y-4">
                {beneficios.map((beneficio, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-hpe-green flex-shrink-0 mt-1" size={24} />
                    <span className="text-lg text-purple-50">{beneficio}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2032" 
                alt="AI Benefits"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Brain className="text-purple-600 mx-auto mb-6" size={64} />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Comienza tu proyecto de IA
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            ALLTI es partner certificado de HPE con especialistas en infraestructura de IA. 
            Te ayudamos desde el diseño hasta la implementación y operación continua.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contacto" 
              className="bg-purple-600 text-white px-8 py-4 rounded-lg hover:bg-purple-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Agendar Consultoría de IA
            </Link>
            <Link 
              to="/casos-de-uso" 
              className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              Ver Casos de Éxito
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
